/* eslint-disable max-lines */

import { CriteriaI, TriggerI } from './Customer/types';
import React, { Dispatch, SetStateAction } from 'react';
import { IGlobalModelYear, IRecallAffectedModel } from '../../../store/reducers/recall/types';
import { RootState } from '../../../store/rootReducer';

export function numberToOrdinalWord(num: number): string {
  switch (num) {
    case 1:
      return 'First';
    case 2:
      return 'Second';
    case 3:
      return 'Third';
    case 4:
      return 'Fourth';
    case 5:
      return 'Fifth';
    case 6:
      return 'Sixth';
    case 7:
      return 'Seventh';
    case 8:
      return 'Eighth';
    case 9:
      return 'Ninth';
    case 10:
      return 'Tenth';
    default:
      return num.toString();
  }
}

export const validateGroup = <T>(arr: T[], validator: (el: T) => boolean): boolean => {
  return arr.length === 0 || arr.every(validator);
};

export const states = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WI',
  'WY',
];

// helpers/validationHelpers.ts
export const validateCriteriaOperator = (
  criterias: CriteriaI[],
  setCriteriaOperatorErrors: Dispatch<
    SetStateAction<{
      [index: number]: boolean;
    }>
  >
) => {
  const errors: { [index: number]: boolean } = {};
  criterias.forEach((c, i) => {
    if (!c.operator) errors[i] = true;
  });
  setCriteriaOperatorErrors(errors);
  return errors;
};

export const validateCriteriaType = (
  criterias: CriteriaI[],
  setCriteriaTypeErrors: Dispatch<
    SetStateAction<{
      [index: number]: boolean;
    }>
  >
) => {
  const errors: { [index: number]: boolean } = {};
  criterias.forEach((c, i) => {
    if (!c.type) errors[i] = true;
  });
  setCriteriaTypeErrors(errors);
  return errors;
};

export const validateRuleOperator = (
  rules: CriteriaI[],
  setRuleOperatorErrors: Dispatch<
    SetStateAction<{
      [index: number]: boolean;
    }>
  >
) => {
  const errors: { [index: number]: boolean } = {};
  rules.forEach((r, i) => {
    if (!r.operator) errors[i] = true;
  });
  setRuleOperatorErrors(errors);
  return errors;
};

export const validateRuleType = (
  rules: CriteriaI[],
  setRuleTypeErrors: Dispatch<
    SetStateAction<{
      [index: number]: boolean;
    }>
  >
) => {
  const errors: { [index: number]: boolean } = {};
  rules.forEach((r, i) => {
    if (!r.type) errors[i] = true;
  });
  setRuleTypeErrors(errors);
  return errors;
};

export const validateTriggers = (
  criterias: CriteriaI[],
  triggers: TriggerI[],
  setFirstTriggerDateError: Dispatch<SetStateAction<boolean>>,
  showError: (msg: string) => void,
  setTriggerDateErrors: React.Dispatch<
    React.SetStateAction<{
      [index: number]: boolean;
    }>
  >
) => {
  if (triggers.length === 1 && !triggers[0].scheduledTime) {
    setFirstTriggerDateError(true);
    showError("The 'Scheduled time' selection is required.");
    return true;
  }
  return !validateDaysToFutureAppointment(criterias, triggers, showError, setTriggerDateErrors);
};

export const checkAudienceCriteria = (
  rules: CriteriaI[],
  triggers: TriggerI[],
  criterias: CriteriaI[],
  showError: (msg: string) => void
) => {
  if ((rules.length || triggers.length) && !criterias.length) {
    showError('At least one Audience Criteria should be added.');
    return false;
  }
  return true;
};

export const validateDaysToFutureAppointment = (
  criterias: CriteriaI[],
  triggers: TriggerI[],
  showError: (msg: string) => void,
  setTriggerDateErrors: React.Dispatch<
    React.SetStateAction<{
      [index: number]: boolean;
    }>
  >
) => {
  if (criterias[0]?.type === 'Days To Future Appointment' && triggers.length) {
    const errors: { [index: number]: boolean } = {};

    triggers.forEach((trigger, index) => {
      errors[index] = trigger.daysFromListGeneration > +criterias[0].value;
    });

    const hasInvalid = Object.values(errors).some(Boolean);

    if (hasInvalid) {
      showError('Days from list generation must be less or equal to audience criteria value');
      setTriggerDateErrors(errors);
      return false;
    }

    setTriggerDateErrors({});
  }

  return true;
};

export const validateTriggersSequence = (
  triggers: TriggerI[],
  showError: (msg: string) => void
) => {
  if (!triggers.length) return true;

  const isValid = triggers.every((t, i) => {
    if (i === 0) return true;

    const prev = triggers[i - 1];

    if (t.daysFromListGeneration > prev.daysFromListGeneration) return true;

    if (t.daysFromListGeneration === prev.daysFromListGeneration) {
      const [h1, m1] = prev.scheduledTime.split(':').map(Number);
      const [h2, m2] = t.scheduledTime.split(':').map(Number);

      const prevMinutes = h1 * 60 + m1;
      const currMinutes = h2 * 60 + m2;

      return currMinutes - prevMinutes >= 60;
    }

    return false;
  });

  if (!isValid) {
    showError(
      'Subsequent contact triggers must be at least one hour after the preceding configured contact.'
    );
    return false;
  }

  return true;
};

export const filterValidRulesAndTriggers = (
  criterias: CriteriaI[],
  rules: CriteriaI[],
  triggers: TriggerI[]
) => ({
  filterRules: [...criterias, ...rules].filter(rule => rule.operator && rule.type),
  triggers: triggers.filter(trigger => trigger.scheduledTime),
});

export const VIN_CHECK_API = 'VIN Check (API)';
export const CSV_UPLOADED = 'CSV Uploaded';
export const UPLOAD_CSV = 'Upload CSV';

export const toEnumLabel = <T extends Record<number, string>>(
  value: string,
  enumMap: T
): string => {
  const enumIndex = Number(value);

  if (!Number.isNaN(enumIndex) && enumMap[enumIndex] !== undefined) {
    return enumMap[enumIndex];
  }

  return value;
};

export type TSelectedModelKey = IGlobalModelYear & {
  vehicleCount: number;
};

export const mapModelIdsToGlobalModels = (
  modelIds: number[],
  models: IRecallAffectedModel[]
): TSelectedModelKey[] => {
  const mapped = models
    .filter(item => modelIds.includes(item.globalVehicleModelId))
    .map(item => ({
      globalVehicleModelId: item.globalVehicleModelId,
      year: item.year,
      vehicleCount: item.eligibleVehicleCount || 0,
    }));

  const uniqueByModelAndYear = new Map<string, TSelectedModelKey>();
  mapped.forEach(item => {
    uniqueByModelAndYear.set(`${item.globalVehicleModelId}-${item.year}`, item);
  });

  return Array.from(uniqueByModelAndYear.values());
};

export const mapAllAffectedModelsToSelectedKeys = (
  models: RootState['recalls']['affectedModels']
) => {
  const uniqueByModelAndYear = new Map<string, TSelectedModelKey>();

  models.forEach(({ globalVehicleModelId, year, eligibleVehicleCount }) => {
    uniqueByModelAndYear.set(`${globalVehicleModelId}-${year}`, {
      globalVehicleModelId,
      year,
      vehicleCount: eligibleVehicleCount,
    });
  });

  return Array.from(uniqueByModelAndYear.values());
};
